/**
 * The two chapters that ship inside the app.
 *
 * > *"Only I, and my friend uses this, hence pre upload the following two chapters to one book, so
 * > both of us easily can access this like PDF in the painting area the way we are supposed to,
 * > without adding it ourselves, so make it a part of the app."*
 *
 * Books live in the browser's own IndexedDB — there is no server-side library, which is why
 * "upload it for me" was never possible and why this exists instead. The PDFs are build-time
 * assets under `public/books`, precached by the service worker like any other file in `dist`, so
 * the chapter opens at a desk with no wifi.
 *
 * ── The four rules that make it behave ────────────────────────────────────────────────────
 *
 * **The same path as an upload.** `ingestPdf` is what the file picker calls, and it is what this
 * calls; the only difference in the record is `builtin: true`. If a built-in book needed its own
 * reader, the reader would have a bug.
 *
 * **Idempotent, and versioned by content.** The install is keyed `<id>@<hash>`, where the hash is
 * stamped into the bundle at build time by `vite.config.ts`. Running it twice installs nothing;
 * shipping a corrected scan replaces rather than duplicates.
 *
 * **Deletable, and restorable.** Removing it is remembered, so it is never silently reinstalled
 * behind his back — the Book pane offers to put it back instead.
 *
 * **Off the boot path.** Extracting a 47-page PDF is seconds of worker time. It runs after first
 * paint, and nothing waits on it.
 */
import { ingestPdf } from './book-ingest';
import { deletePdf, listPdfs, savePdf, type DocIndex } from './book-pdf';

export interface BuiltinBook {
  /** Stable across builds: it is the record's id, so a reinstall replaces the same row. */
  id: string;
  file: string;
  name: string;
  /** The exercise range it covers, for the row that offers to restore it. */
  from: number;
  to: number;
}

export const BUILTIN: readonly BuiltinBook[] = [
  { id: 'builtin-ma4-k1', file: 'ma4-kapitel-1.pdf', name: 'Matematik 4 — Kapitel 1, Trigonometri', from: 1001, to: 1143 },
  { id: 'builtin-ma4-k2', file: 'ma4-kapitel-2.pdf', name: 'Matematik 4 — Kapitel 2, Derivata', from: 2001, to: 2153 },
];

/** Filename → content hash, stamped at build time. Absent in a dev server with no files there. */
const HASHES: Record<string, string> = typeof __BOOKS__ === 'object' && __BOOKS__ ? __BOOKS__ : {};

export const hashOf = (book: BuiltinBook): string | null => HASHES[book.file] ?? null;

/** A chapter we do not ship is not an error — it is a folder without the file in it yet. */
export const available = (book: BuiltinBook): boolean => hashOf(book) !== null;

// ── The ledger ─────────────────────────────────────────────────────────────────────────────
//
// Two facts, kept apart on purpose: what is installed (so it is not installed twice) and what was
// REMOVED (so it is not installed again). Collapsing them into one is how a deleted book comes
// back on the next reload.

const INSTALLED_KEY = 'euclid.builtin.installed';
const REMOVED_KEY = 'euclid.builtin.removed';

const readList = (key: string): string[] => {
  try {
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

const writeList = (key: string, values: string[]): void => {
  try { localStorage.setItem(key, JSON.stringify([...new Set(values)])); } catch { /* private window */ }
};

/**
 * Bumped when the READER changes, not the file.
 *
 * The install is keyed by the PDF's content so a corrected scan replaces the old one. That misses
 * the other way an index can be wrong: the bytes stay identical and the code that reads them gets
 * better. `firstPage` recorded the last page a number appeared on, which for anything reprinted in
 * the Facit was the answers rather than the question — already-installed devices would have kept
 * that index forever. Raising this reinstalls once, on the next boot, and never again.
 */
const INGEST_REVISION = 2;

const stamp = (book: BuiltinBook): string =>
  `${book.id}@${hashOf(book) ?? 'none'}#${INGEST_REVISION}`;

export const wasRemoved = (book: BuiltinBook): boolean => readList(REMOVED_KEY).includes(book.id);

/** Remember that this one was deliberately thrown away, so nothing puts it back on its own. */
export function rememberRemoval(id: string): void {
  if (!BUILTIN.some(book => book.id === id)) return;
  writeList(REMOVED_KEY, [...readList(REMOVED_KEY), id]);
  writeList(INSTALLED_KEY, readList(INSTALLED_KEY).filter(entry => !entry.startsWith(`${id}@`)));
}

/** Forget the removal, so `installBuiltins` will put it back. What the restore row presses. */
export function forgetRemoval(id: string): void {
  writeList(REMOVED_KEY, readList(REMOVED_KEY).filter(entry => entry !== id));
}

export const isBuiltin = (doc: Pick<DocIndex, 'id'>): boolean =>
  BUILTIN.some(book => book.id === doc.id);

// ── The install ────────────────────────────────────────────────────────────────────────────

export interface InstallProgress {
  book: BuiltinBook;
  page: number;
  pages: number;
}

export interface InstallOptions {
  /** Put back even the ones that were deleted. What the restore row passes. */
  force?: boolean;
  chapterFor?: (book: BuiltinBook) => { chapterId: string; courseId: string } | null;
  onProgress?: (progress: InstallProgress) => void;
  /** Each chapter as it lands, so a shelf can show the first while the second is still reading. */
  onInstalled?: (book: BuiltinBook) => void;
}

/** Which of the two still need installing, given what is on the device and what was thrown away. */
export async function pending(options: InstallOptions = {}): Promise<BuiltinBook[]> {
  const held = await listPdfs().catch((): DocIndex[] => []);
  const installed = new Set(readList(INSTALLED_KEY));
  const there = new Set(held.map(doc => doc.id));
  return BUILTIN.filter(book => {
    if (!available(book)) return false;
    if (!options.force && wasRemoved(book)) return false;
    // Both halves matter: the ledger says which VERSION is in, and the library says whether the
    // row is still there at all. A book cleared by a wiped database is not installed, whatever
    // the ledger remembers.
    return !(installed.has(stamp(book)) && there.has(book.id));
  });
}

/**
 * Install whatever is missing. Safe to call on every boot; usually does nothing.
 *
 * Returns how many were installed, so a caller can stay quiet when the answer is none.
 */
export async function installBuiltins(options: InstallOptions = {}): Promise<number> {
  const wanted = await pending(options);
  let done = 0;

  for (const book of wanted) {
    try {
      if (options.force) forgetRemoval(book.id);
      const response = await fetch(`${import.meta.env.BASE_URL}books/${book.file}`);
      if (!response.ok) throw new Error(`${response.status} for ${book.file}`);
      const bytes = await response.arrayBuffer();

      /*
       * A missing file on Cloudflare Pages comes back as the SPA shell with a 200.
       *
       * `response.ok` is therefore not evidence that this is a PDF, and handing a page of HTML to
       * the reader fails somewhere deep inside pdf.js with a message about the wrong thing. Every
       * PDF starts `%PDF`; four bytes is the whole check.
       */
      const head = new TextDecoder().decode(new Uint8Array(bytes.slice(0, 4)));
      if (head !== '%PDF') {
        throw new Error(`${book.file} came back as ${head.trim() ? 'something else' : 'nothing'} — `
          + 'this build may be out of date. Reload the page and try again.');
      }

      const where = options.chapterFor?.(book) ?? null;
      const { index } = await ingestPdf({
        bytes,
        name: book.name,
        id: book.id,
        builtin: true,
        chapterId: where?.chapterId ?? '',
        courseId: where?.courseId ?? '',
        onProgress: (page, pages) => options.onProgress?.({ book, page, pages }),
      });

      await savePdf(index, bytes);
      writeList(INSTALLED_KEY, [...readList(INSTALLED_KEY), stamp(book)]);
      done += 1;
      options.onInstalled?.(book);
    } catch (error) {
      // A chapter that will not install must not stop the other one, and must never stop the app.
      // The console keeps the stack; the shelf simply has one fewer book on it.
      console.error(`The built-in chapter ${book.file} could not be installed`, error);
    }
  }

  return done;
}

/** Throw one away for good — the record, the bytes, and the memory that it was ever installed. */
export async function removeBuiltin(id: string): Promise<void> {
  rememberRemoval(id);
  await deletePdf(id);
}
